import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  fetchClients,
  fetchClient,
  createClient,
  updateClient,
  activateClient,
  deactivateClient,
  fetchClientLookup,
} from "../api/clientApi"
import type {
  GetClientListRequest,
  UpsertClientRequest,
  DeactivateClientRequest,
} from "./types"
import { useLookup } from "@/shared/hooks/useLookup"
import { lookupKeys } from "@/shared/queries/lookupKeys"
import { invalidateLookups } from "@/shared/queries/invalidateLookups"

/**
 * Query hook to fetch paginated clients list
 */
export const useClients = (params: GetClientListRequest) => {
  return useQuery({
    queryKey: ['clients', params],
    queryFn: () => fetchClients(params),
  })
}

/**
 * Query hook to fetch a single client by ID
 * @param id - Client ID
 * @param initialData - Optional initial data from router state to avoid initial API call
 */
export const useClientLookup = (ids?: number[], includeSites?: boolean) =>
  useLookup({
    queryKey: lookupKeys.client.byIds(ids, includeSites),
    queryFn: () => fetchClientLookup(ids, includeSites),
    tier: 'standard',
  })

export const useClient = (id: number) => {
  return useQuery({
    queryKey: ['client', id],
    queryFn: () => fetchClient(id),
    enabled: !!id,
  })
}

/**
 * Mutation hook to create a new client
 */
export const useCreateClient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpsertClientRequest) => createClient(request),
    onSuccess: () => {
      // Invalidate clients list to refetch
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success("Client created successfully")
    },
  })
}

/**
 * Mutation hook to update an existing client
 */
export const useUpdateClient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpsertClientRequest }) =>
      updateClient(id, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['client', variables.id] })
      invalidateLookups(queryClient, 'site')
      toast.success("Client updated successfully")
    },
  })
}

/**
 * Mutation hook to activate a client
 */
export const useActivateClient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => activateClient(id),
    onSuccess: (_, id) => {
      // Invalidate both clients list and individual client query
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['client', id] })
      toast.success("Client activated successfully")
    },
  })
}

/**
 * Mutation hook to deactivate a client
 */
export const useDeactivateClient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: DeactivateClientRequest }) =>
      deactivateClient(id, request),
    onSuccess: (_, variables) => {
      // Invalidate both clients list and individual client query
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['client', variables.id] })
      toast.success("Client deactivated successfully")
    },
  })
}
